import { Shield, Lock, Award } from 'lucide-react';

// Compliance badge data with SVG logos
const complianceBadges = [
  {
    shortName: "HIPAA",
    description: "Health Insurance Portability and Accountability Act",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="3"/>
        <path d="M30 35 L50 25 L70 35 L70 55 L50 75 L30 55 Z" fill="currentColor" opacity="0.2"/>
        <path d="M50 30 L50 55 M40 42 L60 42" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
        <circle cx="50" cy="62" r="3" fill="currentColor"/>
      </svg>
    )
  },
  {
    shortName: "GDPR",
    description: "General Data Protection Regulation",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="3"/>
        <circle cx="50" cy="50" r="25" fill="currentColor" opacity="0.2"/>
        <path d="M50 30 L65 50 L50 70 L35 50 Z" fill="none" stroke="currentColor" strokeWidth="3"/>
        <circle cx="50" cy="50" r="8" fill="currentColor"/>
      </svg>
    )
  },
  {
    shortName: "SOC 2",
    description: "Service Organization Control Type 2",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <rect x="15" y="25" width="70" height="50" rx="5" fill="none" stroke="currentColor" strokeWidth="3"/>
        <path d="M35 50 L45 60 L65 40" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <rect x="30" y="30" width="40" height="5" rx="2" fill="currentColor" opacity="0.3"/>
      </svg>
    )
  },
  {
    shortName: "FDA",
    description: "Food and Drug Administration",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="3"/>
        <text x="50" y="58" fontSize="24" fontWeight="bold" textAnchor="middle" fill="currentColor">FDA</text>
      </svg>
    )
  },
  {
    shortName: "HITRUST",
    description: "Health Information Trust Alliance",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <polygon points="50,10 90,35 90,65 50,90 10,65 10,35" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2"/>
        <path d="M40 45 L50 55 L65 35" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    )
  },
  {
    shortName: "ISO",
    description: "Information Security Management",
    logo: (
      <svg viewBox="0 0 100 100" className="w-8 h-8">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="3"/>
        <text x="50" y="45" fontSize="16" fontWeight="bold" textAnchor="middle" fill="currentColor">ISO</text>
        <text x="50" y="65" fontSize="12" textAnchor="middle" fill="currentColor">27001</text>
      </svg>
    )
  }
];

export function ComplianceSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Floating Security Icons */}
      <div className="absolute top-20 left-10 opacity-20">
        <Shield className="w-24 h-24 text-blue-400" />
      </div>
      <div className="absolute bottom-20 right-10 opacity-20">
        <Lock className="w-20 h-20 text-blue-400" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-blue-400" />
            <span className="text-blue-400 font-semibold tracking-wider uppercase text-sm">
              Security & Compliance
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Compliance-Driven Services & Industry Partners
          </h2>
          <p className="text-lg text-blue-100/80 leading-relaxed max-w-3xl mx-auto">
            At Ortem Technologies, our developers are HIPAA-certified and well-versed in key healthcare regulations 
            such as GDPR and HITECH. We specialize in building HIPAA-ready solutions that ensure complete security 
            and compliance with all industry standards. Trust Ortem to deliver robust, compliant healthcare software 
            that safeguards patient data at every level.
          </p>
        </div>

        {/* Compliance Badges Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16">
          {complianceBadges.map((badge, index) => (
            <div 
              key={badge.shortName}
              className="group relative"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center
                            hover:bg-white/20 hover:border-blue-400/50 transition-all duration-300
                            hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 
                              flex items-center justify-center shadow-lg shadow-blue-500/30 text-white">
                  {badge.logo}
                </div>
                <h3 className="text-white font-bold text-lg mb-1">{badge.shortName}</h3>
                <p className="text-blue-200/70 text-xs leading-tight">{badge.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Stats */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className="w-6 h-6 text-blue-400" />
                <span className="text-3xl md:text-4xl font-bold text-white">100%</span>
              </div>
              <p className="text-blue-200/70 text-sm">Upwork Success Rate</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-6 h-6 text-green-400" />
                <span className="text-3xl md:text-4xl font-bold text-white">HIPAA</span>
              </div>
              <p className="text-blue-200/70 text-sm">Certified Developers</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Lock className="w-6 h-6 text-purple-400" />
                <span className="text-3xl md:text-4xl font-bold text-white">SOC 2</span>
              </div>
              <p className="text-blue-200/70 text-sm">Type II Compliant</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-6 h-6 text-cyan-400" />
                <span className="text-3xl md:text-4xl font-bold text-white">GDPR</span>
              </div>
              <p className="text-blue-200/70 text-sm">EU Data Protection</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
